import React from "react";
import Grid from "@mui/material/Grid";
import {
  SectionHeader,
  ReadOnlyField,
  PhoneField,
  PhoneExtensionField,
  EmailField,
  InfoNote,
  PriorSubmissionBanner
} from "../primitives";
import { selectionKey } from "../data";
import { colors } from "../../../utils/styles";

function AuthorizedSignerStep({ selection, options = [], signers = [], onSignerEdit, latestSubmission }) {
  const selected = options.find((o) => o.key === selectionKey(selection));
  const selectedDescriptor = selected?.label ?? null;
  // Drop signer rows whose AGREEMENT_REQ is not "Y" — those agencies don't
  // require a Special Assessments Agreement, so their signer fields are hidden.
  const requiredSigners = signers.filter((s) => s.agreementRequired);
  const agreementNotRequired = selection && signers.length > 0 && requiredSigners.length === 0;

  return (
    <>
      <SectionHeader number="3" title="Authorized Signer" subtitle="Authorized Signer Information" />
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {latestSubmission && <PriorSubmissionBanner submission={latestSubmission} />}

        {agreementNotRequired ? (
          <InfoNote>
            A signed Special Assessment Agreement is not required for{" "}
            {selectedDescriptor ? (
              <strong>{selectedDescriptor}</strong>
            ) : (
              <em style={{ color: colors.textMuted }}>— select a parent agency in Step 1 —</em>
            )}
            ; therefore, Authorized Signer information is not needed.
          </InfoNote>
        ) : (
          <>
            <InfoNote>
              Authorized signer is the person responsible for signing the Special Assessments Agreement for{" "}
              {selectedDescriptor ? (
                <strong>{selectedDescriptor}</strong>
              ) : (
                <em style={{ color: colors.textMuted }}>— select a parent agency in Step 1 —</em>
              )}
              . Contact Property Tax at{" "}
              <a
                href="mailto:ptassessments@sbcountyatc.gov"
                style={{
                  color: colors.navy,
                  fontWeight: 700,
                  textDecoration: "underline",
                  textUnderlineOffset: "2px"
                }}
              >
                ptassessments@sbcountyatc.gov
              </a>{" "}
              if authorized signer needs to be changed.
            </InfoNote>

            {!selection && (
              <InfoNote variant="error">
                Please go back to Step 1 and select a Parent Agency to populate signer information.
              </InfoNote>
            )}

            {selection && requiredSigners.length === 0 && (
              <div
                style={{
                  padding: 16,
                  background: colors.sectionBg,
                  borderRadius: 8,
                  color: colors.textMuted,
                  fontSize: 13
                }}
              >
                No authorized signers configured for this parent agency. Contact{" "}
                <a href="mailto:ptassessments@sbcountyatc.gov">ptassessments@sbcountyatc.gov</a> to add one.
              </div>
            )}

            {requiredSigners.map((signer, index) => (
              <div
                key={index}
                style={{
                  paddingBottom: index < signers.length - 1 ? 24 : 0,
                  borderBottom: index < signers.length - 1 ? `1px solid ${colors.borderLight}` : "none"
                }}
              >
                {signers.length > 1 && (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 8
                    }}
                  >
                    Signer {index + 1}
                  </div>
                )}
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ReadOnlyField label="Agreement Name" value={signer.agreementName} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ReadOnlyField label="Agreement Type" value={signer.agreementType} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ReadOnlyField label="Authorized Signer Name" value={signer.name} accent />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ReadOnlyField label="Authorized Signer Title" value={signer.title} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 3.5 }}>
                    <PhoneField
                      label="Authorized Signer Phone"
                      required
                      path={`signers[${index}].phone`}
                      value={signer.phone || ""}
                      onChange={(e) => onSignerEdit(index, "phone", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
                    <PhoneExtensionField
                      label="Phone Extension"
                      path={`signers[${index}].phoneExtension`}
                      value={signer.phoneExtension || ""}
                      onChange={(e) => onSignerEdit(index, "phoneExtension", e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <EmailField
                      label="Authorized Signer Email"
                      required
                      path={`signers[${index}].email`}
                      value={signer.email || ""}
                      onChange={(e) => onSignerEdit(index, "email", e.target.value)}
                      maxLength={200}
                    />
                  </Grid>
                </Grid>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

export default React.memo(AuthorizedSignerStep);
